export const BASE_URL = "https://www.tamilmurasu.com.sg";

export const CACHE_EXPIRY_TIME = 300;

/**
 * SWR (stale-while-revalidate) TTLs for the latest-videos-playlist cache.
 *
 * `softTtl` is the staleness threshold — once an entry's age crosses it,
 * subsequent reads return the stale data immediately AND fire a single-flight
 * background refresh. `hardTtl` is purely a hygiene floor on Redis eviction;
 * with hardTtl ≫ softTtl, any realistic traffic refreshes the entry well
 * before the eviction window, so the synchronous-fetch path is effectively
 * never taken.
 *
 * 30 min soft-TTL chosen to match TM's actual publishing cadence (1–2 videos
 * per day) — a 5 min refresh cadence would be wasteful here. Bonus: when
 * Phase 2's 15 min cron lands, the cache only goes stale if two consecutive
 * cron firings miss, which is a much wider recovery window than 5 min would
 * give.
 *
 * See BHTM-2999 / "TM Videos Feed — 2-Phase Plan" Confluence.
 */
export const LATEST_VIDEO_PLAYLIST_SOFT_TTL_SECONDS = 1_800;
export const LATEST_VIDEO_PLAYLIST_HARD_TTL_SECONDS = 86_400;

/** Lock TTL for the single-flight refresh lock (per playlist). */
export const LATEST_VIDEO_PLAYLIST_LOCK_TTL_SECONDS = 30;

/** Per-page Brightcove fetch timeout. Bounds the lock-holder's wall time. */
export const BRIGHTCOVE_FETCH_TIMEOUT_MS = 6_000;

// Site Map Constants
export const SIZE_COUNT = 10000;
export const PUB_NAME = "Tamilmurasu";
export const PUB_LANGUAGE = "ta";

/**
 * HTTP Status Codes
 * Standard HTTP status codes used across all API endpoints
 */
export const HTTP_STATUS = {
  SUCCESS: 200,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  INTERNAL_ERROR: 500,
} as const;

export type HttpStatusCode = (typeof HTTP_STATUS)[keyof typeof HTTP_STATUS];

/**
 * HTTP Methods
 */
export enum HttpMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  DELETE = "delete",
  PATCH = "patch",
}

/**
 * Cache Control Headers
 * CloudFront optimized cache headers with stale-while-revalidate
 * and stale-if-error patterns for improved resilience
 */
export const CACHE_CONTROL = {
  CLOUDFRONT: {
    /** Menu data: 30 days fresh, 5min stale-while-revalidate, 1 day stale-if-error */
    HEADER_MENU:
      "public, max-age=2592000, stale-while-revalidate=300, stale-if-error=86400",

    /** Dynamic feeds (videos, word quiz, section): 5min fresh, 5min SWR, 1 day SIE.
     *  SWR widened from 60s to 300s as part of BHTM-2999 — composes with the
     *  BFF's in-process SWR layer so CloudFront serves stale-from-stale rather
     *  than calling back to the BFF on every TTL boundary. */
    DYNAMIC:
      "public, max-age=300, stale-while-revalidate=300, stale-if-error=86400",

    /** Most-read / hourly data: 1hr fresh, 30s SWR, 1 day SIE */
    ONE_HOUR: "max-age=3600, stale-while-revalidate=30, stale-if-error=86400",

    /** Randomised responses (e.g. word quiz): 30s fresh — short enough that a
     *  user completing a round always gets a different set, long enough for CDN
     *  to absorb any burst, 1 day stale-if-error for resilience. */
    RANDOM: "public, max-age=30, stale-if-error=86400",

    /** Video feed initial page (page 0): 30min fresh, 6hr stale-while-revalidate, 1 day stale-if-error.
     *  Applies to /v1/feed/media/portrait-videos, /v1/feed/media/landscape-videos, and
     *  /v1/feed/media/videos when page=0. */
    HALF_AN_HOUR:
      "public, max-age=1800, stale-while-revalidate=21600, stale-if-error=86400",

    /** Video feed paginated requests (page ≥ 1): bypass CloudFront entirely.
     *  Paginated requests call Brightcove directly using offset — no CDN caching. */
    NO_CACHE: "no-store",
  },
} as const;

/**
 * Mobile Platform Constants
 * Used for platform validation in both API endpoints and SSR
 */

/** Valid platform values for mobile APIs */
export const VALID_PLATFORMS = ["ios", "android"] as const;
export type ValidPlatform = (typeof VALID_PLATFORMS)[number];

/** Header name for platform identification */
export const PLATFORM_HEADER = "platform";

/**
 * SSR Cache Configuration
 * Cache max-age (in seconds) for SSR page responses
 */
export const SSR_CACHE_MAX_AGE = 180;
